import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  ThemeProvider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChangeInstalledStockItem,
  InstalledStockItemsQueryParameter,
  InternalStockItem,
  InternalStockItemsQuery
} from "../../inventory/models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { CustomerInternalStockState } from "../models/models";
import {
  changeInternalStockItemsQuery,
  getCategories,
  getInternalStockItems,
  getManufacturers
} from "../customerInternalStockSlice";
import ActionToast from "../../../shared/components/ActionToast";
import { setError, setSuccess } from "../customerMachinerySlice";
import { NotificationMessage } from "../../../shared/models/models";
import FullHeight from "../../../shared/components/FullHeight";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import ImageBox from "../../../shared/components/ImageBox";
import { productCondition } from "../../../common/models/CommonModels";
import TablePagination from "../../../shared/components/tables/TablePagination";
import InternalStockItemsTableToolbar from "../../shared/components/InternalStockItemsTableToolbar";
import LimitedQuantityFormat from "../../../shared/components/formats/LimitedQuantityFormat";
import Loader from "../../../shared/components/Loader";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface InternalStockDialogProps {
  machineId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (value: ChangeInstalledStockItem[]) => void;
}

const InternalStockDialog = ({ machineId, open, onClose, onConfirm }: InternalStockDialogProps) => {
  const { internalStockItemsQuery, categories, manufacturers, internalStockItems, loading, success, error } =
    useAppSelector<CustomerInternalStockState>((state: any) => state.customerInternalStock);

  const dispatch = useAppDispatch();

  const [selectedItems, setSelectedItems] = useState<ChangeInstalledStockItem[]>([]);
  const [errors, setErrors] = useState<Record<string, { error: boolean; message: string }>>({});
  const hasErrors = Object.values(errors).some((error) => error.error);
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const getErrorState = (value: number, maxQuantity: number) => {
    if (value < 1) {
      return { error: true, message: translate("minValue") };
    }
    if (value > maxQuantity) {
      return { error: true, message: translate("maxValue", { max: maxQuantity }) };
    }
    return { error: false, message: "" };
  };

  const buttonText = () => {
    const selectedEmpty = selectedItems.length === 0;
    const isSelectedItems =
      selectedItems.length === 1 ? translate("attachOneItem") : translate("attachManyItems", { count: selectedItems.length });
    return selectedEmpty ? translate("attachItems") : isSelectedItems;
  };

  const handleChangeSelection = () => {
    if (internalStockItems?.items) {
      if (selectedItems.length === internalStockItems.items.length) {
        setSelectedItems([]);
        return;
      }

      let items: ChangeInstalledStockItem[] = [];
      for (let i = 0; i < internalStockItems.items.length; i++) {
        let item = selectedItems.find((x) => x.stockItemId === internalStockItems.items[i].id);
        if (item) {
          items.push(item);
          continue;
        }
        item = { stockItemId: internalStockItems.items[i].id, quantity: 0 };
        items.push(item);
      }

      setSelectedItems(items);
    }
  };

  const handleStockItemSelect = (stockItem: InternalStockItem) => {
    setErrors((prev) => ({
      ...prev,
      [stockItem.id]: { error: false, message: "" }
    }));
    let item = selectedItems.find((x) => x.stockItemId === stockItem.id);
    if (item) {
      setSelectedItems([...selectedItems.filter((x) => x.stockItemId !== stockItem.id)]);
    } else {
      item = { stockItemId: stockItem.id, quantity: 0 };
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleChangeQuantity = (item: InternalStockItem, value: number) => {
    const newItem = selectedItems.find((x) => x.stockItemId === item.id);

    if (item.inStockQuantity.value) {
      const errorState = getErrorState(value, item.inStockQuantity.value);
      setErrors((prev) => ({
        ...prev,
        [item.id]: errorState
      }));

      if (!errorState.error && newItem) {
        newItem.quantity = value;
      }
    }
  };

  const init = async () => {
    const newQuery: InternalStockItemsQuery = { ...internalStockItemsQuery, machineId: machineId };
    changeInternalStockItemsQuery(newQuery);
    await dispatch(getCategories());
    await dispatch(getManufacturers());
    await dispatch(getInternalStockItems(newQuery));
  };

  useEffect(() => {
    if (open) {
      init();
    }
  }, [open]);

  const columns: TableColumnDefinition[] = [
    { title: translate("product"), parameter: InstalledStockItemsQueryParameter.number },
    { title: translate("brandCategory"), parameter: InstalledStockItemsQueryParameter.manufacturer },
    { title: tCommon("condition") },
    { title: translate("installedInStock") },
    { title: translate("addToMachine") }
  ];

  const onSubmit = () => {
    const confirmModel = selectedItems.filter((x) => {
      if (x.quantity) {
        return x.quantity;
      } else {
        return x.quantity = 1;
      }
    });
    onConfirm(confirmModel);
    setSelectedItems([]);
  };

  const handleQueryChange = (value: InternalStockItemsQuery) => {
    dispatch(changeInternalStockItemsQuery(value));
    dispatch(getInternalStockItems(value));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = internalStockItemsQuery.sortBy !== parameter ? false : !internalStockItemsQuery.sortDesc;
    const newQuery = { ...internalStockItemsQuery, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeInternalStockItemsQuery(newQuery));
    dispatch(getInternalStockItems(newQuery));
  };

  const handlePageSizeChange = (size: number) => {
    const newQuery = { ...internalStockItemsQuery, pageNo: 1, pageSize: size };
    dispatch(changeInternalStockItemsQuery(newQuery));
    dispatch(getInternalStockItems(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...internalStockItemsQuery, pageNo: no };
    dispatch(changeInternalStockItemsQuery(newQuery));
    await dispatch(getInternalStockItems(newQuery));
  };

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            },

            ".MuiPaper-root": {
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "90%",
            maxWidth: "90%"
          }
        }}
      >
        <DialogTitle>
          <p className="heading-2">{translate("selectItemsToAttach")}</p>
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "#333333"
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Box>
            <ActionToast
              open={!!success}
              variant="success"
              message={success || ""}
              onClose={() => dispatch(setSuccess(undefined))}
              duration={6000}
            />

            <ActionToast
              open={!!error}
              variant="error"
              message={NotificationMessage.error(error).message || ""}
              onClose={() => dispatch(setError(undefined))}
              duration={6000}
            />

            <InternalStockItemsTableToolbar
              query={internalStockItemsQuery}
              categories={categories}
              manufacturers={manufacturers}
              onChange={handleQueryChange}
            />

            {loading && (
              <Box className="my-5">
                <FullHeight>
                  <Loader />
                </FullHeight>
              </Box>
            )}

            {!loading && !internalStockItems?.items.length && (
              <FullHeight>
                <Box>
                  <p className="text-16-medium mt-5 flex justify-center">{translate("noItems")}</p>
                </Box>
              </FullHeight>
            )}

            {!loading && !!internalStockItems?.items.length && (
              <FullHeight>
                <Box>
                  <Table>
                    <SortTableHead
                      columns={columns}
                      sortBy={internalStockItemsQuery.sortBy}
                      sortDesc={internalStockItemsQuery.sortDesc}
                      onSort={handleSort}
                      checkboxSelection={true}
                      isChecked={selectedItems?.length === internalStockItems.items.length}
                      onChangeCheckbox={handleChangeSelection}
                    />

                    <TableBody>
                      {internalStockItems?.items.map((item) => (
                        <TableRow
                          key={item.id}
                          selected={!!selectedItems.find((x) => x.stockItemId === item.id)}
                          sx={{ height: "78px" }}
                        >
                          <TableCell padding="checkbox">
                            <Box sx={{ minWidth: "70px" }}>
                              <Checkbox
                                key={item?.id}
                                color="primary"
                                checked={!!selectedItems.find((x) => x.stockItemId === item.id)}
                                onChange={(event) => {
                                  event.stopPropagation();
                                  handleStockItemSelect(item);
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <ImageBox size="small" src={item.files?.length ? item.files[0].content : undefined} />
                              <Box className="ms-3">
                                <Box className="text-14-medium" color={"#333333"}>
                                  {item.product?.number}
                                </Box>
                                <Box className="text-12-medium" lineHeight={"17px"}>
                                  {item.alternativeNumber}
                                </Box>
                              </Box>
                            </div>
                          </TableCell>

                          <TableCell>
                            <Box className="text-14-medium uppercase" color={"#333333"}>
                              {item.product?.manufacturer?.name}
                            </Box>
                            <Box className="text-14-regular" color={"#747881"}>
                              {item.product?.category?.name}
                            </Box>
                          </TableCell>

                          <TableCell>{tCommon(`productCondition.${productCondition(item.conditionCode)}`)}</TableCell>

                          <TableCell>
                            <LimitedQuantityFormat value={item.installedItemsQuantity} limit={item.inStockQuantity} />
                          </TableCell>

                          <TableCell sx={{ width: "20%" }}>
                            {selectedItems.find((x) => x.stockItemId === item.id) && (
                              <TextField
                                key={item.id}
                                type="number"
                                name={item.id}
                                className="w-full"
                                sx={{
                                  width: "130px",
                                  ".MuiOutlinedInput-root": {
                                    height: "36px !important",
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "4px"
                                  }
                                }}
                                defaultValue={1}
                                onChange={(event: any) => {
                                  handleChangeQuantity(item, Number(event.target.value));
                                }}
                                error={!!errors[item.id]?.error}
                                helperText={errors[item.id]?.message || ""}
                                inputProps={{ min: 1, max: item.inStockQuantity.value }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <TablePagination
                  variant="bottom"
                  pageNo={internalStockItemsQuery.pageNo}
                  pageSize={internalStockItemsQuery.pageSize}
                  totalCount={internalStockItems?.totalCount}
                  onPageNoChange={handlePageNoChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </FullHeight>
            )}
          </Box>
        </DialogContent>
        <DialogActions className="flex mt-2" sx={{ alignItems: "end" }}>
          <Button variant="text" onClick={onClose} sx={{ minWidth: "220px" }}>
            {tCommon("buttons.cancel")}
          </Button>

          <Button
            form="form"
            type="submit"
            variant="contained"
            onClick={onSubmit}
            sx={{ minWidth: "220px" }}
            disabled={hasErrors || selectedItems.length < 1}
          >
            {buttonText()}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default InternalStockDialog;